import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { listRestaurants } from '../../services/restaurants';
import {
  issueMarketplaceExtensionPairingCode,
  listMarketplaceExtensionInstallations,
  MARKETPLACE_PLATFORM_LABELS,
  MARKETPLACE_PLATFORMS,
  revokeMarketplaceExtensionInstallation,
  type ConfigurableMarketplacePlatform,
  type MarketplaceExtensionInstallation,
  type MarketplacePlatform,
  type PairingCodeResult,
} from '../../services/marketplaceExtensions';

const INSTALLATIONS_QUERY_KEY = ['operations', 'marketplace-extension', 'installations'] as const;
const ACTIVE_WINDOW_MS = 5 * 60 * 1000;
const QUIET_WINDOW_MS = 24 * 60 * 60 * 1000;

const dateFormatter = new Intl.DateTimeFormat('tr-TR', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Europe/Istanbul',
});

const getPlatformLabel = (platform: MarketplacePlatform) => MARKETPLACE_PLATFORM_LABELS[platform];

type Feedback = { type: 'success' | 'error'; text: string } | null;

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
};

const installationPlatforms = (
  installation: MarketplaceExtensionInstallation,
): MarketplacePlatform[] => {
  if (installation.allowedPlatforms.length > 0) return installation.allowedPlatforms;
  return Array.from(new Set(installation.accounts.map((account) => account.platform)));
};

const getInstallationHealth = (installation: MarketplaceExtensionInstallation) => {
  if (installation.revokedAt) {
    return { label: 'İptal edildi', className: 'danger', detail: formatDate(installation.revokedAt) };
  }

  if (installation.expiresAt && new Date(installation.expiresAt).getTime() <= Date.now()) {
    return { label: 'Süresi doldu', className: 'danger', detail: formatDate(installation.expiresAt) };
  }

  if (!installation.lastSeenAt) {
    return { label: 'Eşleşme bekliyor', className: 'neutral', detail: 'Henüz sinyal yok' };
  }

  const age = Date.now() - new Date(installation.lastSeenAt).getTime();
  if (Number.isFinite(age) && age <= ACTIVE_WINDOW_MS) {
    return { label: 'Aktif', className: 'success', detail: 'Son 5 dakika içinde görüldü' };
  }
  if (Number.isFinite(age) && age <= QUIET_WINDOW_MS) {
    return { label: 'Sessiz', className: 'warning', detail: 'Bugün sinyal alındı' };
  }
  return { label: 'Bağlantı yok', className: 'danger', detail: '24 saattir sinyal alınmadı' };
};

const latestSyncText = (installation: MarketplaceExtensionInstallation): string => {
  const sync = installation.latestSync;
  if (!sync) return 'Henüz veri alınmadı';

  const countParts: string[] = [];
  if (typeof sync.counts.inserted === 'number') {
    countParts.push(`${sync.counts.inserted} eklendi`);
  }
  if (typeof sync.counts.updated === 'number' && sync.counts.updated > 0) {
    countParts.push(`${sync.counts.updated} güncellendi`);
  }
  if (typeof sync.counts.unchanged === 'number' && sync.counts.unchanged > 0) {
    countParts.push(`${sync.counts.unchanged} değişmedi`);
  }
  if (typeof sync.counts.rejected === 'number' && sync.counts.rejected > 0) {
    countParts.push(`${sync.counts.rejected} reddedildi`);
  }

  return countParts.length > 0 ? countParts.join(' · ') : sync.status || 'Veri alındı';
};

export default function MarketplaceExtensionManager() {
  const queryClient = useQueryClient();
  const [restaurantId, setRestaurantId] = useState('');
  const [allowedPlatforms, setAllowedPlatforms] = useState<ConfigurableMarketplacePlatform[]>(
    MARKETPLACE_PLATFORMS.map((platform) => platform.id),
  );
  const [externalRestaurantIds, setExternalRestaurantIds] = useState<
    Record<ConfigurableMarketplacePlatform, string>
  >({ trendyol: '', getir: '', migros: '' });
  const [label, setLabel] = useState('');
  const [pairingResult, setPairingResult] = useState<PairingCodeResult | null>(null);
  const [issueFeedback, setIssueFeedback] = useState<Feedback>(null);
  const [installationFeedback, setInstallationFeedback] = useState<Feedback>(null);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const restaurantsQuery = useQuery({
    queryKey: ['operations', 'marketplace-extension', 'restaurants'],
    queryFn: listRestaurants,
  });
  const restaurants = useMemo(() => restaurantsQuery.data ?? [], [restaurantsQuery.data]);

  useEffect(() => {
    if (!restaurantId && restaurants.length > 0) {
      setRestaurantId(restaurants[0].id);
    }
  }, [restaurantId, restaurants]);

  const installationsQuery = useQuery({
    queryKey: [...INSTALLATIONS_QUERY_KEY, restaurantId],
    queryFn: () => listMarketplaceExtensionInstallations(restaurantId),
    enabled: Boolean(restaurantId),
    refetchInterval: 30_000,
  });
  const installations = useMemo(
    () => installationsQuery.data ?? [],
    [installationsQuery.data],
  );

  const selectedRestaurant = useMemo(
    () => restaurants.find((restaurant) => restaurant.id === restaurantId) ?? null,
    [restaurantId, restaurants],
  );

  const activeInstallations = useMemo(
    () => installations.filter((installation) => !installation.revokedAt).length,
    [installations],
  );

  const issueMutation = useMutation({
    mutationFn: issueMarketplaceExtensionPairingCode,
    onSuccess: (result) => {
      setPairingResult(result);
      setCopyStatus(null);
      setIssueFeedback({
        type: 'success',
        text: 'Tek kullanımlık eşleştirme kodu oluşturuldu.',
      });
      void queryClient.invalidateQueries({
        queryKey: [...INSTALLATIONS_QUERY_KEY, result.restaurantId],
      });
    },
    onError: (error: unknown) => {
      setIssueFeedback({
        type: 'error',
        text: error instanceof Error ? error.message : 'Eşleştirme kodu oluşturulamadı.',
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: revokeMarketplaceExtensionInstallation,
    onMutate: (installationId) => {
      setRevokingId(installationId);
      setInstallationFeedback(null);
    },
    onSuccess: () => {
      setInstallationFeedback({ type: 'success', text: 'Eklenti bağlantısı iptal edildi.' });
      void queryClient.invalidateQueries({
        queryKey: [...INSTALLATIONS_QUERY_KEY, restaurantId],
      });
    },
    onError: (error: unknown) => {
      setInstallationFeedback({
        type: 'error',
        text: error instanceof Error ? error.message : 'Bağlantı iptal edilemedi.',
      });
    },
    onSettled: () => setRevokingId(null),
  });

  const handleRestaurantChange = (nextRestaurantId: string) => {
    setRestaurantId(nextRestaurantId);
    setExternalRestaurantIds({ trendyol: '', getir: '', migros: '' });
    setPairingResult(null);
    setCopyStatus(null);
    setIssueFeedback(null);
    setInstallationFeedback(null);
  };

  const togglePlatform = (platform: ConfigurableMarketplacePlatform) => {
    setAllowedPlatforms((current) =>
      current.includes(platform)
        ? current.filter((item) => item !== platform)
        : [...current, platform],
    );
    setPairingResult(null);
    setCopyStatus(null);
  };

  const handleIssue = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIssueFeedback(null);
    setPairingResult(null);
    setCopyStatus(null);

    if (!restaurantId) {
      setIssueFeedback({ type: 'error', text: 'Önce bir restoran seçin.' });
      return;
    }
    if (allowedPlatforms.length === 0) {
      setIssueFeedback({ type: 'error', text: 'En az bir pazar yeri seçin.' });
      return;
    }
    const missingPlatform = allowedPlatforms.find(
      (platform) => !externalRestaurantIds[platform].trim(),
    );
    if (missingPlatform) {
      setIssueFeedback({
        type: 'error',
        text: `${getPlatformLabel(missingPlatform)} restoran/mağaza ID alanını doldurun.`,
      });
      return;
    }

    issueMutation.mutate({
      restaurantId,
      allowedPlatforms,
      externalRestaurantIds: Object.fromEntries(
        allowedPlatforms.map((platform) => [platform, externalRestaurantIds[platform].trim()]),
      ),
      label: label.trim() || undefined,
    });
  };

  const handleCopy = async () => {
    if (!pairingResult) return;
    try {
      await navigator.clipboard.writeText(pairingResult.pairingCode);
      setCopyStatus('Kod panoya kopyalandı.');
    } catch {
      setCopyStatus('Kod kopyalanamadı; kodu seçip elle kopyalayın.');
    }
  };

  const handleRevoke = (installation: MarketplaceExtensionInstallation) => {
    const installationName = installation.label || installation.deviceLabel || 'bu eklenti';
    const confirmed = window.confirm(
      `${installationName} bağlantısı iptal edilsin mi? Bu kurulum yeniden eşleştirilmeden veri gönderemez.`,
    );
    if (confirmed) revokeMutation.mutate(installation.id);
  };

  return (
    <div className="marketplace-bridge-stack">
      <div className="card-grid two-column tight marketplace-bridge-setup">
        <section className="card" aria-labelledby="marketplace-pairing-title">
          <div className="card-header">
            <div>
              <h2 className="card-title" id="marketplace-pairing-title">
                Yeni eklenti eşleştir
              </h2>
              <p className="card-subtitle">
                Kod yalnızca seçtiğiniz restoran ve pazar yerleri için yetki verir.
              </p>
            </div>
          </div>

          <form className="form-grid" onSubmit={handleIssue}>
            <label className="form-field" htmlFor="marketplace-restaurant">
              <span>Restoran</span>
              <select
                id="marketplace-restaurant"
                value={restaurantId}
                onChange={(event) => handleRestaurantChange(event.target.value)}
                disabled={restaurantsQuery.isLoading || issueMutation.isPending}
                required
              >
                <option value="">Restoran seçin</option>
                {restaurants.map((restaurant) => (
                  <option key={restaurant.id} value={restaurant.id}>
                    {restaurant.name} — {restaurant.city}/{restaurant.district}
                  </option>
                ))}
              </select>
            </label>

            <fieldset className="form-fieldset marketplace-platform-fieldset">
              <legend>İzin verilen pazar yerleri</legend>
              <div className="checkbox-grid">
                {MARKETPLACE_PLATFORMS.map((platform) => (
                  <label className="checkbox-item" key={platform.id}>
                    <input
                      type="checkbox"
                      checked={allowedPlatforms.includes(platform.id)}
                      onChange={() => togglePlatform(platform.id)}
                      disabled={issueMutation.isPending}
                    />
                    <span>{platform.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {allowedPlatforms.map((platform) => (
              <label className="form-field" htmlFor={`marketplace-store-${platform}`} key={platform}>
                <span>{getPlatformLabel(platform)} restoran/mağaza ID</span>
                <input
                  id={`marketplace-store-${platform}`}
                  type="text"
                  value={externalRestaurantIds[platform]}
                  onChange={(event) => {
                    const value = event.target.value;
                    setExternalRestaurantIds((current) => ({ ...current, [platform]: value }));
                    setPairingResult(null);
                    setCopyStatus(null);
                  }}
                  placeholder="Pazar yeri panelindeki sabit işletme kimliği"
                  maxLength={160}
                  autoComplete="off"
                  disabled={issueMutation.isPending}
                  required
                />
                <span className="helper-text">
                  Eklenti yalnızca bu kimliği taşıyan pazar yeri yanıtlarını yükler.
                </span>
              </label>
            ))}

            <label className="form-field" htmlFor="marketplace-device-label">
              <span>Kurulum etiketi (isteğe bağlı)</span>
              <input
                id="marketplace-device-label"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="Örn. Lido merkez kasa"
                maxLength={80}
                autoComplete="off"
                disabled={issueMutation.isPending}
              />
              <span className="helper-text">Cihazı daha sonra kolayca tanımanızı sağlar.</span>
            </label>

            {issueFeedback ? (
              <p className={`status-banner ${issueFeedback.type}`} role="status">
                {issueFeedback.text}
              </p>
            ) : null}

            <div className="form-actions">
              <button
                type="submit"
                className="button primary"
                disabled={
                  issueMutation.isPending ||
                  restaurantsQuery.isLoading ||
                  !restaurantId ||
                  allowedPlatforms.length === 0 ||
                  allowedPlatforms.some((platform) => !externalRestaurantIds[platform].trim())
                }
              >
                {issueMutation.isPending ? 'Kod oluşturuluyor…' : 'Tek Kullanımlık Kod Oluştur'}
              </button>
            </div>
          </form>

          {restaurantsQuery.isError ? (
            <p className="status-banner error" role="alert">
              Restoranlar yüklenemedi. Sayfayı yenileyip tekrar deneyin.
            </p>
          ) : null}

          {pairingResult ? (
            <div className="marketplace-pairing-result" aria-live="polite">
              <div>
                <p className="marketplace-pairing-eyebrow">Tek kullanımlık eşleştirme kodu</p>
                <output className="marketplace-pairing-code">
                  {pairingResult.pairingCode}
                </output>
              </div>
              <button type="button" className="button secondary" onClick={() => void handleCopy()}>
                Kodu Kopyala
              </button>
              <p className="helper-text marketplace-pairing-expiry">
                {selectedRestaurant?.name ?? 'Seçili restoran'} · Son kullanım:{' '}
                {formatDate(pairingResult.expiresAt)}. Bu kod daha sonra tekrar görüntülenmez.
              </p>
              {copyStatus ? <p className="helper-text" role="status">{copyStatus}</p> : null}
            </div>
          ) : null}
        </section>

        <aside className="card" aria-labelledby="marketplace-guide-title">
          <div>
            <h2 className="card-title" id="marketplace-guide-title">Nasıl bağlanır?</h2>
            <p className="card-subtitle">Eşleştirme yaklaşık bir dakika sürer.</p>
          </div>
          <ol className="marketplace-guide-list">
            <li>Chrome eklentisini kurun ve eklenti ayarlarında Supabase Functions adresini kaydedin.</li>
            <li>Oluşturduğunuz kodu eklentinin <strong>Eşleştir</strong> ekranına yapıştırın.</li>
            <li>Yetkili pazar yeri panellerini aynı Chrome profilinde açıp giriş yapın.</li>
            <li>Paneller açıkken eklenti sipariş yanıtlarını güvenli biçimde Lido’ya aktarır.</li>
          </ol>
          <div className="info-banner">
            Eklenti sizden pazar yeri parolası istemez. Eşleştirme kodunu yalnızca kurulumu yaptığınız
            cihazda kullanın; kodu mesajla veya e-postayla paylaşmayın.
          </div>
        </aside>
      </div>

      <section className="card" aria-labelledby="marketplace-installations-title">
        <div className="card-header">
          <div>
            <h2 className="card-title" id="marketplace-installations-title">Eklenti bağlantıları</h2>
            <p className="card-subtitle">
              {selectedRestaurant
                ? `${selectedRestaurant.name} için ${installations.length} kurulum · ${activeInstallations} iptal edilmemiş`
                : 'Kurulumları görmek için restoran seçin.'}
            </p>
          </div>
          <button
            type="button"
            className="button ghost"
            onClick={() => void installationsQuery.refetch()}
            disabled={!restaurantId || installationsQuery.isFetching}
          >
            {installationsQuery.isFetching ? 'Yenileniyor…' : 'Yenile'}
          </button>
        </div>

        {installationFeedback ? (
          <p className={`status-banner ${installationFeedback.type}`} role="status">
            {installationFeedback.text}
          </p>
        ) : null}

        {installationsQuery.isLoading ? (
          <div className="placeholder-card">Eklenti bağlantıları yükleniyor…</div>
        ) : installationsQuery.isError ? (
          <div className="status-banner error" role="alert">
            Eklenti bağlantıları yüklenemedi. Yenile düğmesiyle tekrar deneyin.
          </div>
        ) : !restaurantId ? (
          <div className="empty-state">Önce bir restoran seçin.</div>
        ) : installations.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz eşleştirilmiş eklenti yok</strong>
            <p>Yukarıdaki formdan tek kullanımlık bir kod oluşturarak başlayın.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table" aria-label="Pazar yeri eklenti bağlantıları">
              <thead>
                <tr>
                  <th>Kurulum</th>
                  <th>Pazar yerleri</th>
                  <th>Durum</th>
                  <th>Son sinyal</th>
                  <th>Son veri aktarımı</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {installations.map((installation) => {
                  const health = getInstallationHealth(installation);
                  const syncDate =
                    installation.latestSync?.finishedAt ?? installation.latestSync?.startedAt;
                  return (
                    <tr key={installation.id}>
                      <td>
                        <strong>
                          {installation.label || installation.deviceLabel || 'Adsız kurulum'}
                        </strong>
                        <div className="helper-text">
                          {installation.extensionVersion
                            ? `Eklenti v${installation.extensionVersion}`
                            : 'Eklenti sürümü bildirilmedi'}
                        </div>
                        <div className="helper-text">Oluşturuldu: {formatDate(installation.createdAt)}</div>
                      </td>
                      <td>
                        <div className="chip-group">
                          {installationPlatforms(installation).map((platform) => (
                            <div key={platform}>
                              <span className="chip neutral">
                                {getPlatformLabel(platform)}
                              </span>
                              <div className="helper-text">
                                ID: {installation.externalRestaurantIds[platform] ?? 'bildirilmedi'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span className={`chip ${health.className}`}>{health.label}</span>
                        <div className="helper-text">{health.detail}</div>
                      </td>
                      <td>{formatDate(installation.lastSeenAt)}</td>
                      <td>
                        <div>{latestSyncText(installation)}</div>
                        <div className="helper-text">{formatDate(syncDate)}</div>
                        {installation.latestSync?.errorMessage ? (
                          <div className="error-text">Son aktarım hata verdi.</div>
                        ) : null}
                      </td>
                      <td>
                        {installation.revokedAt ? (
                          <span className="helper-text">İşlem yapılamaz</span>
                        ) : (
                          <button
                            type="button"
                            className="button danger"
                            onClick={() => handleRevoke(installation)}
                            disabled={revokeMutation.isPending}
                          >
                            {revokingId === installation.id ? 'İptal ediliyor…' : 'Bağlantıyı İptal Et'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="helper-text marketplace-refresh-note">
          Durum bilgisi 30 saniyede bir otomatik yenilenir. “Aktif”, eklentinin son beş dakika içinde
          sunucuya sinyal gönderdiğini belirtir.
        </p>
      </section>
    </div>
  );
}
